import { Button, useTranslation } from '@hbs-us/ui';
import { BarChart3 } from 'lucide-react';
import { downloadCredentialed, openCredentialed } from '../../../lib/download.js';
import { buildReportExportLinks } from '../exportLinks.js';
import type { ReportsSearch } from '../searchParams.js';

/**
 * Page head (legacy ReportsPage 65–78), uplifted to the P22 redesign grammar
 * (D-04 re-skin): the brand chart glyph + "Reports" title, the resolved filter
 * label subtitle, and the Export CSV / Print View actions. Functionality is
 * unchanged — only the title block gains the consistent leading glyph used
 * across the redesigned screens.
 *
 * The actions TRIGGER the api-host download paths (D-06) — Export CSV is a
 * credentialed `<a download>`, Print View opens the api-host print path in a
 * new tab (`window.open(_, '_blank', 'noreferrer')`). No export logic is built
 * client-side. Both buttons are `secondary` (not the orange accent — the accent
 * is reserved for the active range pill / Apply per the Color contract).
 */
export function ReportsHeader({
  search,
  filterLabel,
}: {
  search: ReportsSearch;
  filterLabel?: string;
}) {
  const { t } = useTranslation();
  const links = buildReportExportLinks(search);

  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="flex items-center gap-2 font-display font-semibold text-3xl text-foreground">
          <BarChart3 className="size-7 text-muted-foreground" aria-hidden="true" />
          {t('reports:title')}
        </h1>
        {filterLabel ? (
          <p className="mt-1 font-mono text-muted-foreground text-sm">{filterLabel}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            void downloadCredentialed(links.csvUrl, 'reports.csv').catch((err) =>
              console.error('reports csv download failed', err),
            )
          }
        >
          {t('reports:actions.exportCsv')}
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            void openCredentialed(links.printUrl).catch((err) =>
              console.error('reports print failed', err),
            )
          }
        >
          {t('reports:actions.printView')}
        </Button>
      </div>
    </header>
  );
}
